import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';
import {Database} from '@tnc/shared';

import {FlattenT} from '@/lib/types/utility.types';

import {getQueryKey} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';

/*******************************************************************
 * Types
 *******************************************************************/
type UseViewQueryPropsT<
  V extends keyof Database['public']['Views'],
  // We use "any" here as the TData is unknown, but an unknown type will cause an invalid error in the ide
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TError extends Error = Error,
  TQueryFnData = TData,
  TQueryKey extends QueryKey = QueryKey,
> = FlattenT<
  Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>> & {
    view: V;
    enabled?: boolean;
    protect?: boolean;
  }
>;

/**
 * Fetches a view from the database.
 * @template {Database['public']['Views']} V - The view to fetch data from
 * @param {V} view - The view to fetch data from
 * @returns {Promise<Database['public']['Views'][V]['Row']>} - The data from the view
 */
async function fetchView<
  V extends keyof Database['public']['Views'],
  R extends object = Database['public']['Views'][V]['Row'],
>(view: V) {
  return SupabaseInstance.supabase
    .from(view)
    .select<string, R>()
    .single<R>()
    .then<R>(({data, error}) => {
      if (error) throw error;
      if (!data) throw new Error('No data returned');
      return data;
    });
}

/**
 * Fetches a view from the database.
 * @param {V} view - The view to fetch data from
 * @param {boolean} enabled - Whether the query is enabled
 * @returns {ReturnType<typeof useQuery<Database['public']['Views'][V]['Row']>>} - The query
 */
export const useViewQuery = <
  V extends keyof Database['public']['Views'],
  R extends object = Database['public']['Views'][V]['Row'],
>({
  view,
  enabled: _enabled = true,
  protect,
  ...rest
}: UseViewQueryPropsT<V>) => {
  const {isAdministrator, isAuthenticated} = useAuth();
  const enabled = (protect ? isAdministrator && _enabled : _enabled) && isAuthenticated;

  const queryFn: QueryFunction<R, QueryKey> = async () => {
    return fetchView<V, R>(view);
  };

  const queryOptions: UseQueryOptions<R, Error, R, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey: getQueryKey.stats(view, enabled),
    queryFn,
  };

  return useQuery(queryOptions);
};
